import { ReactNode, useMemo } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { ENCDE_SECRET } from "store/api/constApi";
import { encode } from "utils/HelperFunctions";
import { decodeLegacyUrlValue } from "utils/legacyUrlDecoder";
import { INVALID_PATH, invalidRouteState } from "utils/invalidRoute";

const CLUSTER_ID_PATTERN = /^[1-9]\d{0,19}$/;

type LegacyClusterRedirectProps = {
  children: ReactNode;
};

export const createCanonicalClusterSlug = (
  legacyClusterSlug: string | undefined,
  legacyCodes: string = ENCDE_SECRET
): string | null => {
  if (!legacyClusterSlug || legacyClusterSlug.startsWith("ci_")) {
    return null;
  }

  const decodedClusterId = decodeLegacyUrlValue(legacyClusterSlug, legacyCodes);

  if (!decodedClusterId || !CLUSTER_ID_PATTERN.test(decodedClusterId)) {
    return null;
  }

  return encode(decodedClusterId, "ci_");
};

export const replaceClusterSlugInPath = (
  pathname: string,
  canonicalClusterSlug: string
): string => {
  const pathSegments = pathname.split("/");
  pathSegments[1] = canonicalClusterSlug;
  return pathSegments.join("/");
};

const LegacyClusterRedirect = ({ children }: LegacyClusterRedirectProps) => {
  const { clusterId } = useParams<{ clusterId: string }>();
  const location = useLocation();
  const canonicalClusterSlug = useMemo(
    () => createCanonicalClusterSlug(clusterId),
    [clusterId]
  );

  if (clusterId?.startsWith("ci_")) {
    return <>{children}</>;
  }

  if (!canonicalClusterSlug) {
    return (
      <Navigate
        to={INVALID_PATH}
        replace={true}
        state={invalidRouteState("not-found")}
      />
    );
  }

  return (
    <Navigate
      to={{
        pathname: replaceClusterSlugInPath(
          location.pathname,
          canonicalClusterSlug
        ),
        search: location.search,
        hash: location.hash,
      }}
      replace={true}
    />
  );
};

export default LegacyClusterRedirect;
